import { Map, List, Set } from 'immutable'
import { createSelector } from 'reselect'
import { truncateWords } from 'utils/Helpers'
import { denormalize } from 'normalizr'
import moment from 'moment-timezone'
import * as models from './models'
import {
  PlaybookStatus,
  PlaybookReportType,
  RoutingType,
  ValidationType,
  AnswerType,
  CtaTypes,
  END_CONVERSATION_OPTION,
  PROCEED_OPTION,
  MarketingEmailSubTypes,
  NEW_EDITOR_PLAYBOOK_TYPES,
  EditSenderLevels,
  isLeadBotPlaybook,
  isEmailPlaybook
} from 'app/modules/playbook/constants'
import * as schema from './schema'
import * as actions from './actions'

import { classifications } from 'app/modules/classification/selectors'
import { makeHasGate } from 'app/modules/gates/selectors'

const root = state => state.get('playbooks')

const rootStatus = state => root(state).get('status')
export const saveFailed = state => [actions.savePlaybookFailed.toString(), actions.savePlaybookDraftFailed.toString()].includes(rootStatus(state))

export const isPlaybooks = state => root(state).get('normalized', Map()).size
export const getEndUserEmail = state => root(state).get('endUserEmail')

const getSendTestEmailStatus = createSelector(root, root => root.get('sendTestEmailStatus'))
export const getSendTestEmailLoading = createSelector(
  getSendTestEmailStatus,
  sendTestEmailStatus => sendTestEmailStatus === actions.sendTestEmailPending.toString()
)

export const getSendTestEmailId = createSelector(root, root => root.get('sendTestEmailId'))

export const getEnrolledPlaybookId = createSelector(root, root => root.get('enrolledPlaybookId'))
export const getPlaybooksById = state => root(state).get('byIds')
export const getPlaybookIsPending = (state, playbookId) =>
  getPlaybooksById(state).get(playbookId) === 'PENDING'
const enrolledSequenceEnrollmentInfo = state => root(state).get('enrolledSequenceCount')

export const enrolledSequenceCount = state => enrolledSequenceEnrollmentInfo(state).get('count')
export const enrollmentLimit = state => enrolledSequenceEnrollmentInfo(state).get('limit')
export const enrollmentPeriod = state => enrolledSequenceEnrollmentInfo(state).get('refreshRate')

const fetchEnrolledSequenceCountStatus = state => root(state).get('fetchEnrolledSequenceCountStatus')
export const enrolledSequenceCountLoaded = state => {
  return fetchEnrolledSequenceCountStatus(state) === actions.fetchEnrolledSequenceCountSuccess.toString()
}

export const playbookForEndUser = state => root(state).get('playbookForEndUser')

export const editingPlaybookId = state => root(state).get('editingPlaybookId')

const fetchPlaybookForEndUserStatus = state => root(state).get('fetchPlaybookForEndUserStatus')
export const fetchPlaybookForEndUserPending = state =>
  fetchPlaybookForEndUserStatus(state) === actions.fetchPlaybookForEndUserPending.toString()

const enrollStatusByEmail = state => root(state).get('enrollStatusByEmail')
export const enrollErrorsByEmail = state => root(state).get('enrollErrorsByEmail')

const enrollHasOccurred = state => !enrollStatusByEmail(state).isEmpty()
export const enrollSucceeded = state => {
  return (
    enrollHasOccurred(state) &&
    enrollStatusByEmail(state).every(status => status === actions.enrollSuccess.toString())
  )
}
export const enrollFailed = state => {
  return (
    enrollHasOccurred(state) &&
    enrollStatusByEmail(state).some(status => status === actions.enrollFailed.toString())
  )
}
export const enrollPending = state => {
  return (
    enrollHasOccurred(state) &&
    enrollStatusByEmail(state).some(status => status === actions.enrollPending.toString())
  )
}

export const enrollingPlaybookId = state => root(state).get('enrollingPlaybookId')

const entities = createSelector(root, root => root.getIn(['normalized', 'entities'], Map()))
export const playbooks = createSelector(entities, entities => entities.get('playbooks', Map()))
export const allGoals = createSelector(entities, entities => entities.get('goals', Map()))
export const actionTypes = createSelector(entities, entities => entities.get('actionTypes', Map()))
export const getPlaybook = createSelector(
  [playbooks, (state, props) => props.playbookId],
  (playbooks, playbookId) => playbooks.get(`${playbookId}`, models.Playbook({ id: playbookId }))
)

const normalizedResult = createSelector(root, root => root.getIn(['normalized', 'result'], Set()))
export const playbookList = createSelector(normalizedResult, playbooks, (normalizedResult, playbooks) =>
  normalizedResult
    .toList()
    .map(String)
    .map(result => playbooks.get(result))
)
export const denormalizedPlaybookList = createSelector(
  normalizedResult,
  entities,
  (normalizedResult, entities) =>
    normalizedResult
      .toList()
      .map(String)
      .map(result => {
        const denormalizedPlaybook = denormalize(result, schema.playbook, entities)
        if (!_.isEmpty(denormalizedPlaybook.get('targeting'))) {
          return denormalizedPlaybook.updateIn(['targeting', 'display'], display =>
            display.map(
              ({ conditions }) => conditions.map(condition => condition.toJS()).map(({ id, ...rest }) => rest) // remove id
            )
          )
        }
        return denormalizedPlaybook
      })
)
export const normalizedStatus = createSelector(root, root => root.get('normalizedStatus'))
export const listNeverFetched = state => normalizedStatus(state) === false
export const isListLoading = createSelector(
  normalizedStatus,
  normalizedStatus => normalizedStatus === actions.searchPlaybooksPending.toString()
)

export const activePlaybooksList = createSelector(denormalizedPlaybookList, playbooks => {
  return playbooks.filter(
    playbook => playbook.status === PlaybookStatus.ENABLED || playbook.status === PlaybookStatus.DISABLED
  )
})
export const outboundPlaybooksList = createSelector(activePlaybooksList, playbooks => {
  return playbooks.filter(playbook => {
    if (!playbook.targeting) {return false}
    return playbook.targeting
      .get('display')
      .flatten(false) // false == deep flatten
      .find(condition => condition.field === 'outboundSession')
  })
})
export const playbooksListExcludingOutbound = createSelector(
  activePlaybooksList,
  outboundPlaybooksList,
  (playbooks, outboundPlaybooks) => playbooks.filter(pb => !outboundPlaybooks.includes(pb))
)
export const enabledPlaybooksList = createSelector(playbookList, playbooks => {
  return playbooks.filter(playbook => playbook.status === PlaybookStatus.ENABLED)
})
export const disabledPlaybooksList = createSelector(playbookList, playbooks =>
  playbooks.filter(playbook => playbook.status === PlaybookStatus.DISABLED)
)
export const archivedPlaybooksList = createSelector(playbookList, playbooks =>
  playbooks.filter(playbook => playbook.status === PlaybookStatus.ARCHIVED)
)
export const scheduledPlaybooksList = createSelector(playbookList, playbooks => {
  return playbooks.filter(playbook => playbook.status === PlaybookStatus.SCHEDULED)
})
export const sentPlaybooksList = createSelector(playbookList, playbooks => {
  return playbooks.filter(playbook => playbook.status === PlaybookStatus.SENT)
})
export const emailPlaybooks = createSelector(playbookList, playbooks => {
  const email_playbook_types = [
    PlaybookReportType.SEND_AN_AUTOMATED_EMAIL,
    PlaybookReportType.SEND_A_ONETIME_EMAIL,
    PlaybookReportType.OUTBOUND_SEQUENCE,
    PlaybookReportType.MARKETING_SEQUENCE,
    PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE,
    PlaybookReportType.BLOG_NURTURE,
    PlaybookReportType.NEW_BLOG_CONTENT,
    PlaybookReportType.NEWSLETTER,
    PlaybookReportType.PRODUCT_ANNOUNCEMENT,
    PlaybookReportType.ABANDONED_BOOK_A_MEETING,
  ]
  return playbooks.filter(playbook => email_playbook_types.includes(playbook.reportType))
})
export const activeEmailPlaybooks = createSelector(emailPlaybooks, playbooks =>
  playbooks.filter(playbook => [PlaybookStatus.ENABLED, PlaybookStatus.DISABLED].includes(playbook.status))
)

export const draftRoot = createSelector(root, root => root.getIn(['draft', 'entities'], Map()))
export const draftId = createSelector(root, root => root.getIn(['draft', 'result']))
export const draftLoading = createSelector(draftId, draftId => !draftId)

export const draft = createSelector(draftId, draftRoot, (draftId, draftRoot) =>
  draftRoot.getIn(['playbooks', `${draftId}`])
)

export const draftMeta = createSelector(draft, draft => draft.get('meta'))
export const isDriftLinkOnly = createSelector(draftMeta, meta => meta.get('driftLinkOnly', false))

const isLocalOnlySignature = sig => sig.signatureId === null
const removeSignatureIds = sig => sig.delete('id').delete('signatureId')

export const denormalizedDraft = createSelector(draftId, draftRoot, (draftId, draftEntities) => {
  const denormalized = denormalize(draftId, schema.playbook, draftEntities).updateIn(
    ['targeting', 'display'],
    display =>
      display.map(
        ({ conditions }) => conditions.map(condition => condition.toJS()).map(({ id, ...rest }) => rest) // remove id
      )
  )
  if (denormalized.getIn(['actions', '0', 'action', 'cta', 'type']) === CtaTypes.NONE) {
    return denormalized.setIn(['actions', '0', 'action', 'cta'], null).toJS()
  }

  if (denormalized.getIn(['actions', '0', 'action']).has('signatureActions')) {
    return denormalized.set('actions',
      denormalized.actions.updateIn(['0', 'action', 'signatureActions'], sigs => sigs.map(sig => isLocalOnlySignature(sig) ? removeSignatureIds(sig) : sig))
    ).toJS()
  }

  return denormalized.toJS()
})

export const status = createSelector(draftId, draftRoot, (draftId, draftRoot) =>
  draftRoot.getIn(['playbooks', `${draftId}`, 'status'])
)
export const isEnabled = createSelector(status, status => status === PlaybookStatus.ENABLED)
export const isArchived = createSelector(status, status => status === PlaybookStatus.ARCHIVED)

export const hasEnterpriseActions = createSelector(
  makeHasGate('premium_enterprise'),
  makeHasGate('PLAYBOOKS'),
  (hasEnterprise, hasLegacyPlaybooksGate) => hasEnterprise || hasLegacyPlaybooksGate
)

export const reportType = createSelector(draftId, draftRoot, (draftId, draftRoot) =>
  draftRoot.getIn(['playbooks', `${draftId}`, 'reportType'])
)
export const isLeadBot = createSelector(reportType, isLeadBotPlaybook)

export const isSignaturePlaybook = createSelector(reportType, reportType => reportType === PlaybookReportType.SIGNATURE)
export const getIsOutboundSequencePlaybook = createSelector(
  reportType,
  reportType => reportType === PlaybookReportType.OUTBOUND_SEQUENCE
)
export const getIsSequencePlaybook = createSelector(
  reportType,
  reportType =>
    reportType === PlaybookReportType.OUTBOUND_SEQUENCE ||
    reportType === PlaybookReportType.MARKETING_SEQUENCE ||
    reportType === PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE ||
    reportType === PlaybookReportType.BLOG_NURTURE ||
    reportType === PlaybookReportType.NEW_BLOG_CONTENT ||
    reportType === PlaybookReportType.NEWSLETTER ||
    reportType === PlaybookReportType.PRODUCT_ANNOUNCEMENT ||
    reportType === PlaybookReportType.ABANDONED_BOOK_A_MEETING
)
export const getIsMarketingSequencePlaybook = createSelector(reportType, reportType =>
  [
    PlaybookReportType.MARKETING_SEQUENCE,
    PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE,
    PlaybookReportType.BLOG_NURTURE,
    PlaybookReportType.NEW_BLOG_CONTENT,
    PlaybookReportType.NEWSLETTER,
    PlaybookReportType.PRODUCT_ANNOUNCEMENT,
    PlaybookReportType.ABANDONED_BOOK_A_MEETING,
  ].includes(reportType)
)
export const isMessageTypePlaybook = createSelector(
  isLeadBot,
  getIsSequencePlaybook,
  isSignaturePlaybook,
  (isLeadBot, isSequencePlaybook, isSig) => !isLeadBot && !isSequencePlaybook && !isSig
)
export const isCaptureTypePlaybook = createSelector(
  reportType,
  reportType => reportType === PlaybookReportType.BUILD_A_SUBSCRIBER_LIST
)
export const isCtaTypePlaybook = createSelector(reportType, reportType =>
  [
    PlaybookReportType.DRIVE_TRAFFIC_TARGETED_MESSAGE,
    PlaybookReportType.PROMOTION_FULL_SCREEN_TAKEOVER,
    PlaybookReportType.SEND_AN_AUTOMATED_EMAIL,
    PlaybookReportType.SEND_A_ONETIME_EMAIL,
    PlaybookReportType.EMPTY_SLIDER,
    PlaybookReportType.EMPTY_TAKEOVER,
  ].includes(reportType)
)
//TODO: no longer valid
export const isChatTypePlaybook = createSelector(reportType, reportType =>
  [PlaybookReportType.MORE_SALES_CONVERSATIONS, PlaybookReportType.EMPTY_WELCOME_MESSAGE].includes(reportType)
)
export const getIsOneTimeEmailPlaybook = createSelector(reportType, reportType =>
  [
    PlaybookReportType.SEND_A_ONETIME_EMAIL,
    PlaybookReportType.MARKETING_ONE_TIME_SEQUENCE,
    PlaybookReportType.NEW_BLOG_CONTENT,
    PlaybookReportType.NEWSLETTER,
    PlaybookReportType.PRODUCT_ANNOUNCEMENT,
  ].includes(reportType)
)
export const getIsEmailPlaybook = createSelector(
  reportType,
  isEmailPlaybook
)

export const isOutboundPlaybook = createSelector(reportType, reportType => {
  return [PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS].includes(reportType)
})

export const getDisabledOutboundPlaybooks = state => root(state).getIn(['playbooksByReportType', 'data', PlaybookReportType.TALK_TO_YOUR_OUTBOUND_PROSPECTS, 'disabled'])

export const isABMPlaybook = createSelector(reportType, reportType =>
  [PlaybookReportType.ABM].includes(reportType)
)

export const hasAutoCloseAction = hasEnterpriseActions
export const hasCalendarAction = hasEnterpriseActions

export const goals = createSelector(draftRoot, draftRoot => draftRoot.get('goals', Map()))
export const firstGoal = createSelector(goals, goals => goals.first())
export const orderedGoals = createSelector(goals, draft, (goals, draft) =>
  draft.get('goals').map(goalId => goals.get(goalId))
)

export const draftActionTypes = createSelector(draftRoot, draftRoot => draftRoot.get('actionTypes', Map()))
export const getFirstActionType = createSelector(
  draftActionTypes,
  actionTypes => actionTypes.first() || Map()
)
export const firstAction = createSelector(getFirstActionType, firstActionType =>
  firstActionType.get('action', Map())
)
export const playbookActionInboxId = createSelector(firstAction, firstAction =>
  firstAction.get('inboxId')
)
export const marketingEmailLayout = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'marketingEmailLayout'])
)
export const sequenceDynamicSenderFormat = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'dynamicSenderFormat'])
)
export const sequenceDynamicSendAttribute = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'dynamicAccountSendAttribute'])
)
export const sequenceSenderEmailOverride = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'senderEmailOverride'])
)
export const sequenceSendFromOverride = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'sendFromOverride'])
)
export const sequenceWeekdaysOnly = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'weekdaysOnly'])
)
export const sequenceSmartSending = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'smartSending'])
)

export const sequenceAuthorId = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'authorId'])
)
export const marketingEmailSubType = createSelector(firstAction, firstAction =>
  firstAction.getIn(['options', 'marketingEmailSubType'])
)
export const getIsOneTimeMarketingSequencePlaybook = createSelector(
  marketingEmailSubType,
  subType => subType === MarketingEmailSubTypes.ONE_TIME
)
export const getIsAutomatedMarketingEmailPlaybook = state =>
  marketingEmailSubType(state) === MarketingEmailSubTypes.AUTOMATED
export const dynamicSenderFormat = createSelector(firstAction, firstAction =>
  firstAction.get('dynamicSenderFormat')
)
export const dynamicAccountSendAttribute = createSelector(firstAction, firstAction =>
  firstAction.get('dynamicAccountSendAttribute')
)
export const senderEmailOverride = createSelector(firstAction, firstAction =>
  firstAction.get('senderEmailOverride')
)
export const sendFromOverride = createSelector(firstAction, firstAction =>
  firstAction.get('sendFromOverride')
)
export const authorId = createSelector(firstAction, firstAction => firstAction.get('authorId'))
export const isChatCta = createSelector(
  draftActionTypes,
  draftActionTypes =>
    !!draftActionTypes.map(a => a.getIn(['action', 'cta', 'type'])).filter(t => t === CtaTypes.CHAT).size
)
export const isMeetingCta = createSelector(
  draftActionTypes,
  draftActionTypes =>
    !!draftActionTypes.map(a => a.getIn(['action', 'cta', 'type'])).filter(t => t === CtaTypes.MEETING).size
)

export const getHasRoutingAction = createSelector(
  isCtaTypePlaybook,
  isCaptureTypePlaybook,
  isChatCta,
  (isCtaTypePlaybook, isCaptureTypePlaybook, isChatCta) =>
    (!isCtaTypePlaybook && !isCaptureTypePlaybook) || (isCtaTypePlaybook && isChatCta)
)

export const interaction = createSelector(firstAction, firstAction => models.Interaction(firstAction))
export const welcomeMessage = createSelector(firstAction, firstAction => firstAction.toJS())
export const targetedMessage = createSelector(firstAction, firstAction => firstAction.toJS())
export const takeoverMessage = createSelector(firstAction, firstAction => firstAction.toJS())
export const emailMessage = createSelector(firstAction, firstAction => firstAction.toJS())
export const emailCaptureMessage = createSelector(firstAction, firstAction => firstAction.toJS())
export const sequence = createSelector(firstAction, firstAction => models.Sequence(firstAction))

export const slug = createSelector(interaction, interaction => interaction.slug)
export const getLastQuestionId = createSelector(interaction, interaction => interaction.questions.last())

export const getLastEmailId = createSelector(sequence, sequence => sequence.emails.last())

export const answers = createSelector(draftRoot, draftRoot => draftRoot.get('answers', Map()))
export const getAnswer = (state, props) => answers(state).get(props.id)

export const questions = createSelector(draftRoot, draftRoot => draftRoot.get('questions'))
export const getQuestion = (state, props) => questions(state).get(props.id)
export const numQuestions = createSelector(questions, questions => questions.size)
export const getIsDefaultAnswer = (state, props) => {
  const answer = answers(state).get(props.id)
  const question = questions(state).get(answer.questionId)
  return question.defaultAnswerId === answer.id
}

export const emails = createSelector(draftRoot, draftRoot => draftRoot.get('emails'))
export const getEmail = (state, props) => emails(state).get(`${props.id}`)

export const isEmailCaptureQuestion = createSelector(
  (state, question) => question,
  answers,
  (question, answers) => {
    const emailCaptureAnswers = answers.filter(
      answer =>
        question.answers.contains(answer.id) &&
        answer.type === AnswerType.VALIDATE &&
        answer.validationType === ValidationType.EMAIL
    )
    return emailCaptureAnswers.size > 0
  }
)
export const isPhoneCaptureQuestion = createSelector(
  (state, question) => question,
  answers,
  (question, answers) => {
    const phoneCaptureAnswers = answers.filter(
      answer =>
        question.answers.contains(answer.id) &&
        answer.type === AnswerType.VALIDATE &&
        answer.validationType === ValidationType.PHONE
    )
    return phoneCaptureAnswers.size > 0
  }
)

export const hasEmailCaptureQuestion = createSelector(state => state, questions, (state, questions) => {
  const emailCaptureQuestions = questions.filter(question => isEmailCaptureQuestion(state, question))
  return emailCaptureQuestions.size > 0
})
export const hasPhoneCaptureQuestion = createSelector(state => state, questions, (state, questions) => {
  const phoneCaptureQuestions = questions.filter(question => isPhoneCaptureQuestion(state, question))
  return phoneCaptureQuestions.size > 0
})

export const keywords = createSelector(draftRoot, draftRoot => draftRoot.get('keywords'))
export const getKeyword = (state, props) => keywords(state).get(props.id)

const ui = createSelector(root, root => root.get('ui'))
export const isStatusLoading = createSelector(ui, uiRoot => {
  return uiRoot.get('statusLoading', false)
})
// this assumes you won't be uploading multiple banners at once in a signature playbook, which may not be correct
export const getSignatureBannerStatus = (state) => ui(state).get('signatureBannerUploadStatus')
export const signatureBannerStatusPending = (state) => [
  actions.setSignatureBanner.toString(),
  actions.setSignatureBannerPending.toString()
].includes(getSignatureBannerStatus(state))
export const signatureBannerStatusSuccess = (state) => getSignatureBannerStatus(state) === actions.setSignatureBannerSuccess.toString()
export const signatureBannerStatusFailed = (state) => getSignatureBannerStatus(state) === actions.setSignatureBannerFailed.toString()

export const getEditSenderLevel = state => ui(state).get('editSenderLevel', EditSenderLevels.GLOBAL)

const asyncButtonPress = state => ui(state).get('asyncButtonPress', Map())
export const asyncButtonPending = (state, clickId) =>
  asyncButtonPress(state).getIn([`${clickId}`, 'status']) === actions.asyncButtonPressPending.toString()

const getSendTestSequenceStatus = state => ui(state).get('sendTestSequenceStatus', false)
export const getSendTestSequencePending = state =>
  getSendTestSequenceStatus(state) === actions.sendTestSequencePending.toString()

export const isTemplateContactPreviewLoading = createSelector(ui, uiRoot =>
  uiRoot.get('templateContactPreviewLoading', false)
)
export const isAutosaveActive = createSelector(ui, uiRoot => {
  return uiRoot.get('autosave', false)
})
export const pendingPlaybookChanges = createSelector(ui, uiRoot => {
  return uiRoot.get('doPlaybookChanges', false)
})
export const isSaved = createSelector(ui, uiRoot => {
  return uiRoot.get('saved')
})
export const bannerUploadPending = state => state.getIn(['playbooks', 'ui', 'bannerUploadPending'], Map())

export const isBannerUploadPending = createSelector(
  [bannerUploadPending, (state, emailId) => emailId],
  (bannerUploadPending, emailId) => bannerUploadPending.get(emailId, false)
)
export const pendingDraftStatus = (ui,
uiRoot => {
  return uiRoot.get('doPending', false)
})
export const priorityOrderedPlaybookIds = createSelector(ui, ui => ui.get('priorityOrderedPlaybookIds', []))
export const playbookCounts = createSelector(ui, ui => ui.get('counts', Map()))
export const isSaving = createSelector(ui, ui => ui.get('saving'))
export const isFetching = createSelector(ui, ui => ui.get('fetching'))
export const getSendEmailConfirmModalVisible = createSelector(ui, ui =>
  ui.get('sendEmailConfirmModalVisible')
)
export const getAnswerModalVisible = createSelector(ui, ui => ui.get('answerModalVisible'))
export const getQuestionAdvancedModalVisible = createSelector(ui, ui =>
  ui.get('QuestionAdvancedModalVisible')
)
export const getRenderedEmailPreview = createSelector(
  [ui, (state, playbookId) => playbookId],
  (ui, playbookId) => ui.getIn(['emailRenderedPreviews', playbookId])
)
export const playbookIdForCampaignIdFailed = (state, campaignId) =>
  state.getIn(['playbooks', 'ui', 'campaignIdToPlaybookIdMap', campaignId]) === null
export const playbookIdForCampaignIdLoading = (state, campaignId) =>
  state.getIn(['playbooks', 'ui', 'campaignIdToPlaybookIdMap', campaignId]) === 'PENDING'
export const playbookIdForCampaignId = (state, campaignId) =>
  state.getIn(['playbooks', 'ui', 'campaignIdToPlaybookIdMap', campaignId])
export const getGoalActionModalVisible = createSelector(ui, ui => ui.get('goalActionModalVisible'))
export const getEditSenderModalVisible = createSelector(ui, ui => ui.getIn(['editSenderModal', 'visible']))
export const getEditSenderModalEditingEmailId = createSelector(ui, ui =>
  ui.getIn(['editSenderModal', 'editingEmailId'])
)
export const getTokenModalVisible = createSelector(ui, ui => ui.get('tokenModalVisible'))
export const getCtaButtonModalVisible = createSelector(ui, ui => ui.get('ctaButtonModalVisible'))
export const getCalendarLinkModalVisible = createSelector(ui, ui => ui.get('calendarLinkVisible'))
export const getGoogleAuthModalVisible = createSelector(ui, ui => ui.get('googleAuthModalVisible'))
export const getGoalRoutingModalVisible = createSelector(ui, ui => ui.get('goalRoutingModalVisible'))
export const getExtensionInviteModalVisible = createSelector(ui, ui => ui.get('extensionInviteModalVisible'))
export const segmentSelectVisible = createSelector(ui, ui => ui.get('segmentSelectVisible'))
export const getConditionModalVisible = createSelector(ui, ui => ui.get('conditionModalVisible'))
export const getScheduleModalVisible = createSelector(ui, ui => ui.get('scheduleModalVisible'))
export const sequenceInviteModalVisible = createSelector(ui, ui => ui.get('sequenceInviteModalVisible'))
export const calendarProfileLoading = createSelector(ui, ui => ui.get('calendarProfileLoading'))
export const cardinalityLoading = createSelector(ui, ui => ui.get('cardinalityLoading'))
export const targetingCardinality = createSelector(ui, ui => ui.get('targetingCardinality'))
export const getDisableSalesSequenceModalVisible = createSelector(ui, ui => ui.get('disableSalesSequenceModalVisible'))

export const currentGoalId = createSelector(ui, ui => ui.get('currentGoalId'))
export const currentGoal = createSelector(
  goals,
  currentGoalId,
  (goals, currentGoalId) => goals.get(currentGoalId) || models.Goal()
)
export const currentGoalRouting = createSelector(
  currentGoal,
  currentGoal => currentGoal.get('routing') || models.RoutingOption()
)
export const isCurrentGoalRoutingSingle = createSelector(
  currentGoalRouting,
  currentGoalRouting => currentGoalRouting && currentGoalRouting.type === RoutingType.SINGLE
)
export const selectedGoalAction = createSelector(ui, ui => ui.get('selectedGoalAction'))
export const selectedGoalRoutingOption = createSelector(
  ui,
  ui => ui.get('selectedGoalRoutingOption') || models.RoutingOption()
)
export const isSingleRoutingSelected = createSelector(
  selectedGoalRoutingOption,
  selectedGoalRoutingOption => selectedGoalRoutingOption.get('type') === RoutingType.SINGLE
)
export const goalRoutingView = createSelector(ui, ui => ui.get('goalRoutingView'))
export const isGroupRoutingSelected = createSelector(
  selectedGoalRoutingOption,
  selectedGoalRoutingOption => selectedGoalRoutingOption.get('type') === RoutingType.GROUP
)
export const isGlobalRoutingSelected = createSelector(
  selectedGoalRoutingOption,
  selectedGoalRoutingOption => selectedGoalRoutingOption.get('type') === RoutingType.GLOBAL
)
export const isNoRoutingSelected = createSelector(
  selectedGoalRoutingOption,
  selectedGoalRoutingOption => selectedGoalRoutingOption.get('type') === null
)

export const getCurrentConditionId = createSelector(ui, ui => ui.get('currentConditionId'))

const getCurrentAnswerId = createSelector(ui, ui => ui.get('currentAnswerId'))
export const getCurrentAnswer = createSelector(getCurrentAnswerId, answers, (currentAnswerId, answers) =>
  answers.get(currentAnswerId)
)
export const keywordsByAnswerId = answerId => state =>
  getAnswer(state, { id: answerId })
    .get('keywords')
    .map(keywordId => getKeyword(state, { id: keywordId }))
const getCurrentQuestionId = createSelector(ui, ui => ui.get('currentQuestionId'))
export const getCurrentQuestion = createSelector(
  questions,
  getCurrentQuestionId,
  (questions, currentQuestionId) => questions.find(q => q.id === currentQuestionId)
)
export const getCurrentQuestionByAnswer = createSelector(
  getCurrentAnswerId,
  questions,
  (currentAnswerId, questions) => questions.find(question => question.answers.includes(currentAnswerId))
)

export const getQuestionIndex = (state, props) =>
  interaction(state).questions.findIndex(questionId => questionId === props.id)

export const buttonOnlyResponses = (state, questionId) =>
  (questions(state).find(q => q.id === questionId) || Map()).get('isButtonOnly', false)
export const keywordAnswersForQuestion = (state, questionId) =>
  answers(state).filter(a => a.questionId === questionId && a.type === AnswerType.KEYWORDS)
export const questionHasKeywordAnswers = (state, questionId) =>
  !keywordAnswersForQuestion(state, questionId).isEmpty()
export const numKeywordsInQuestion = (state, questionId) =>
  keywordAnswersForQuestion(state, questionId).reduce(
    (numKeywords, answer) => numKeywords + (answer.keywords.size || answer.keywords.length || 0),
    0
  )

export const getCurrentAnswerHasSpecialKeyword = createSelector(
  getCurrentAnswerId,
  keywords,
  (currentAnswerId, keywords) => {
    return keywords.some(keyword => keyword.special && keyword.answerId === currentAnswerId)
  }
)

const goalSkipToOptions = createSelector(draft, goals, classifications, (draft, goals, classifications) => {
  return draft
    .get('goals')
    .map(goalId => goals.get(goalId))
    .map((goal, i) => {
      const classification = classifications.find(cl => cl.id === goal.classification.get('classificationId'))
      return {
        value: {
          id: goal.id,
          isGoal: true,
        },
        label: `Goal ${i + 1}: ${classification ? classification.name : '...'}`,
      }
    })
})

const questionSkipToOptions = createSelector(
  state => state,
  interaction,
  questions,
  (state, interaction, questions) => {
    const options = interaction.questions.map(questionId => questions.get(questionId)).map((question, i) => {
      const doesEmailCapture = isEmailCaptureQuestion(state, question)
      const doesPhoneCapture = isPhoneCaptureQuestion(state, question)
      const prompt = truncateWords(question.prompt.replace(/<[^>]*>/g, ''), 8)
      return {
        value: {
          id: question.id,
          isGoal: false,
        },
        label: doesEmailCapture
          ? `Email Capture Question ${i + 1}: ${prompt || '...'}`
          : doesPhoneCapture
            ? `Phone Capture Question ${i + 1}: ${prompt || '...'}`
            : `Question ${i + 1}: ${prompt || '...'}`,
      }
    })
    return options
  }
)

export const skipToOptions = createSelector(
  questionSkipToOptions,
  goalSkipToOptions,
  (questionSkipTos, goalSkipTos) => {
    return List()
      .concat(questionSkipTos, goalSkipTos)
      .push(END_CONVERSATION_OPTION)
      .filter(Boolean)
      .toArray()
  }
)

export const skipToOption = answerId =>
  createSelector(skipToOptions, answers, interaction, (skipToOptions, answers, interaction) => {
    const answer = answers.get(answerId)
    const isLastQuestion = interaction.questions.last() === answer.questionId
    if (answer.skipTo) {
      return skipToOptions.find(option => option.value.id === answer.skipTo)
    }
    if (answer.getIn(['skipToGoal', 'goalId'])) {
      const goalOption = skipToOptions.find(
        option => option.value.id === answer.getIn(['skipToGoal', 'goalId'])
      )
      if (goalOption) {return goalOption}
    }
    if (isLastQuestion) {
      return END_CONVERSATION_OPTION
    } else {
      return PROCEED_OPTION
    }
  })

export const currentSkipToOption = state => {
  const answerId = getCurrentAnswerId(state)
  return skipToOption(answerId)(state)
}

const targeting = createSelector(draft, draft => draft.get('targeting') || models.Targeting())
export const audience = createSelector(targeting, targeting => targeting.get('audience'))
export const dueAt = createSelector(targeting, targeting => {
  const dueAt = targeting.get('dueAt')
  if (dueAt === null) {return null}
  const utcDueAt = moment.utc(dueAt)
  return utcDueAt.clone().tz(moment.tz.guess()) // return in local tz
})
export const excludedUserListIds = createSelector(targeting, targeting =>
  targeting.get('excludedUserListIds')
)
export const userListIds = createSelector(targeting, targeting => targeting.get('userListIds'))
export const accountListIds = createSelector(targeting, targeting => targeting.get('accountListIds'))
export const excludedAccountListIds = createSelector(targeting, targeting =>
  targeting.get('excludedAccountListIds')
)
export const builderSmartFilters = createSelector(targeting, targeting => targeting.get('smartFilters'))
export const targetingDisplay = createSelector(targeting, targeting => targeting.get('display'))
export const draftFrequency = createSelector(targeting, targeting =>
  targeting.get('campaignReenrollmentType')
)
export const sequenceUnenrollOnReply = createSelector(targeting, targeting =>
  targeting.get('unenrollOnReply')
)
export const sequenceUnenrollDomainOnReply = createSelector(targeting, targeting =>
  targeting.get('unenrollOnReplyByDomain')
)
export const smartFilters = createSelector(targeting, targeting => targeting.get('smartFilters'))
export const smartFilterEngagedContacts = createSelector(targeting, targeting => {
  // "engagedContacts" is a value shown to the user, behind the scenes, it's a package of:
  // meetingScheduled and salesSequenceEngaged
  return targeting.getIn(['smartFilters', 'meetingScheduled'])
})
export const smartFilterChatEngagedContacts = createSelector(targeting, targeting => {
  return targeting.getIn(['smartFilters', 'chatEngaged'])
})

const getConditionSets = createSelector(draftRoot, draftRoot => draftRoot.get('conditionSets'))
export const getConditionSet = (state, props) => getConditionSets(state).get(props.id)

const getConditions = createSelector(draftRoot, draftRoot => draftRoot.get('conditions'))
export const getFirstCondition = state => getConditions(state).first()
export const getCondition = (state, props) => getConditions(state).get(props.id)
export const getNumConditions = createSelector(getConditions, conditions => conditions.size)

export const playbookReportTypePlans = state => root(state).get('playbookReportTypesByGate')
export const playbookReportTypePlansLoading = state => playbookReportTypePlans(state).get('loading', false)
export const playbookReportTypePlansLoaded = state => playbookReportTypePlans(state).get('loaded', false)
export const getPlaybookReportTypePlansEnabled = state =>
  playbookReportTypePlans(state)
    .get('types', Map())
    .map(gates => gates.length === 0 || gates.some(gate => makeHasGate(gate)(state)))

export const getPlaybookEditorLink = (state, { id, reportType }) =>
  NEW_EDITOR_PLAYBOOK_TYPES.includes(reportType) ? `/playbooks/editor/${id}/` : `/playbooks/${id}/edit`

export const signatureRoot = (state) => root(state).getIn(['ui', 'signatures'], Map())

export const signaturePreviewAll = state => signatureRoot(state).get('previewAll', Map())
export const signaturePreviewAllStatus = state => signaturePreviewAll(state).get('status')
export const signaturePreviewAllFetchPending = state => [actions.fetchSignatureMultiBannerPreview.toString(), actions.fetchSignatureMultiBannerPreviewPending.toString()].includes(signaturePreviewAllStatus(state))
export const signaturePreviewAllFetchFailed = state => actions.fetchSignatureMultiBannerPreviewFailed.toString() === signaturePreviewAllStatus(state)
export const signaturePreviewAllData = state => signaturePreviewAll(state).getIn(['data'], Map())
export const signaturePreviewAllContent = (state, signatureId) => signaturePreviewAll(state).getIn(['data', 'signaturePreview', `${signatureId}`])

export const signatures = createSelector(draftRoot, draft => draft.get('signatureActions'))
export const firstSignature = state => signatures(state).first() || models.Signature()
export const signature = (state, signatureId) => signatures(state).get(signatureId)

export const viewingSignatureId = (state) => firstSignature(state).id

// for now, everything except the banner is shared between signatures in a playbook
export const signatureBody = state => firstSignature(state).get('signatureBody')
export const signatureIncludeAvatar = state => firstSignature(state).get('includeAvatar', false)
export const signatureIncludeSocial = state => firstSignature(state).get('includeCompanySocialMedia', false)
export const signatureIncludeBanner = state => firstSignature(state).get('hasCampaignGraphic', false)

export const signatureBannerImgURL = (state, signatureId) => signatures(state).getIn([`${signatureId}`, 'campaignGraphic'])
export const signatureBannerLinkURL = (state, signatureId) => signatures(state).getIn([`${signatureId}`, 'campaignGraphicLink'])

// TODO: deprecated, remove these soon
export const getSignature = state => models.Signature(firstAction(state))
export const getSignatureBody = state => getSignature(state).get('signatureBody')
export const getSignatureIncludeAvatar = state => getSignature(state).get('includeAvatar', false)
export const getSignatureIncludeSocial = state => getSignature(state).get('includeCompanySocialMedia', false)
export const getSignatureIncludeBanner = state => getSignature(state).get('hasCampaignGraphic', false)
export const getSignatureBannerImgURL = state => getSignature(state).get('campaignGraphic')
export const getSignatureBannerLinkURL = state => getSignature(state).get('campaignGraphicLink')

const campaignIdToPlaybook = state => root(state).get('campaignIdToPlaybook')
const campaignIdToPlaybookStatus = (state, campaignId) => campaignIdToPlaybook(state).getIn([campaignId, 'status'])
export const playbookForCampaignIdNeverFetched = (state, campaignId) => campaignIdToPlaybookStatus(state, campaignId) === undefined
export const playbookForCampaignIdPending = (state, campaignId) => campaignIdToPlaybookStatus(state, campaignId) === actions.fetchPlaybookForCampaignIdPending.toString()
export const playbookForCampaignIdFailed = (state, campaignId) => campaignIdToPlaybookStatus(state, campaignId) === actions.fetchPlaybookForCampaignIdFailed.toString()
export const playbookForCampaignId = (state, campaignId) => campaignIdToPlaybook(state).getIn([campaignId, 'data'])



// WEBPACK FOOTER //
// ./src/scripts/app/modules/playbook/selectors.js